/**
 * Barcode detection from the camera. Uses the browser's BarcodeDetector where it exists
 * (Chrome on Android, macOS, ChromeOS) and otherwise the zxing-wasm polyfill, loaded on
 * demand with its .wasm served from this site (no CDN).
 */

export interface DetectedCode { rawValue: string; format: string }

export interface Detector {
  detect(source: HTMLVideoElement | ImageBitmap | HTMLCanvasElement): Promise<DetectedCode[]>;
}

/** Retail product codes only; QR and 2D codes are never food barcodes. */
export const RETAIL_FORMATS: readonly string[] = ['ean_13', 'ean_8', 'upc_a', 'upc_e'];

interface NativeDetectorCtor {
  new (options: { formats: string[] }): Detector;
  getSupportedFormats(): Promise<string[]>;
}

async function nativeDetector(): Promise<Detector | null> {
  const Ctor = (globalThis as { BarcodeDetector?: NativeDetectorCtor }).BarcodeDetector;
  if (!Ctor) return null;
  try {
    const supported: string[] = await Ctor.getSupportedFormats();
    const formats: string[] = RETAIL_FORMATS.filter((f) => supported.includes(f));
    return formats.length > 0 ? new Ctor({ formats }) : null;
  } catch {
    return null;
  }
}

let cached: Promise<Detector> | null = null;

export function createDetector(): Promise<Detector> {
  cached ??= (async (): Promise<Detector> => {
    const native: Detector | null = await nativeDetector();
    if (native) return native;
    if (__DEMO_ONLY__) throw new Error('Camera scanning needs the full site. Type the number under the barcode instead.');
    const [{ BarcodeDetector, prepareZXingModule }, { default: wasmUrl }] = await Promise.all([
      import('barcode-detector/ponyfill'),
      import('zxing-wasm/reader/zxing_reader.wasm?url'),
    ]);
    prepareZXingModule({ overrides: { locateFile: (path: string, prefix: string) => (path.endsWith('.wasm') ? wasmUrl : prefix + path) } });
    return new BarcodeDetector({ formats: [...RETAIL_FORMATS] as never }) as unknown as Detector;
  })();
  cached.catch(() => { cached = null; });
  return cached;
}

/** A friendly message for getUserMedia failures. */
export function cameraErrorMessage(err: unknown): string {
  const name: string = err instanceof DOMException ? err.name : err instanceof Error ? err.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'Camera access was blocked. Allow the camera for this site in your browser settings, or type the number instead.';
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'No camera was found. Type the number under the barcode instead.';
  if (name === 'NotReadableError') return 'The camera is busy in another app. Close it and try again, or type the number.';
  if (typeof navigator !== 'undefined' && !navigator.mediaDevices) return 'This browser cannot open the camera here (it needs https). Type the number instead.';
  return err instanceof Error ? err.message : 'Could not start the camera.';
}
